import api from './axios';
import axios from './axios';

/**
 * 获取代理账号列表
 * @param {Object} params - 查询参数，包括分页信息和regionId
 * @returns {Promise} - 返回代理账号列表
 */
export const getAgentAccounts = (params) => {
  return api.get('/agents', { params }).then(response => {
    // 处理分页数据结构
    if (response.data && response.data.content && Array.isArray(response.data.content)) {
      return response;
    }

    // 如果不是分页结构但是数组，直接返回
    if (Array.isArray(response.data)) {
      return response;
    }

    // 非预期结构：返回空分页，避免页面渲染崩溃，但保留 HTTP 成功语义
    return {
      ...response,
      data: {
        content: [],
        totalElements: 0,
        totalPages: 0,
        size: params?.size || 10,
        number: params?.page || 0
      }
    };
  });
};

// 创建代理账号
export const createAgentAccount = (accountData) => {
  return api.post('/agents', accountData);
};

// 更新代理账号
export const updateAgentAccount = (id, accountData) => {
  return api.put(`/agents/${id}`, accountData);
};

// 删除代理账号
export const deleteAgentAccount = (id, targetAgentId = null) => {
  const params = targetAgentId ? { targetAgentId } : {};
  return api.delete(`/agents/${id}`, { params });
};

// 获取督学账号列表
export const getMarketingAccounts = (params) => {
  return api.get('/account/marketing', { params });
};

// 获取学生账号列表
export const getStudentAccounts = (params) => {
  return api.get('/account/student', { params });
};

// 获取代理账号统计数据
export const getAgentStats = (params) => {
  return api.get('/agents/stats', params ? { params } : undefined);
};

// 获取代理卡列表
export const getAgentCards = (agentId, params) => {
  return api.get(`/agents/${agentId}/cards`, { params })
    .then(response => {
      // 适配后端PageResponse格式
      if (response.data && response.data.content) {
        return {
          ...response,
          data: {
            content: response.data.content,
            totalElements: response.data.totalElements,
            totalPages: response.data.totalPages,
            size: response.data.size,
            number: response.data.number
          }
        };
      }
      return response;
    });
};

// 添加代理卡
export const addAgentCard = (agentId, cardData) => {
  return api.post(`/agents/${agentId}/cards`, cardData)
    .then(response => {
      // 适配后端ApiResponse格式
      if (response.data && response.data.data) {
        return {
          ...response,
          data: response.data.data
        };
      }
      return response;
    });
};

// 更新代理卡
export const updateAgentCard = (agentId, cardId, cardData) => {
  return api.put(`/agents/${agentId}/cards/${cardId}`, cardData)
    .then(response => {
      // 适配后端ApiResponse格式
      if (response.data && response.data.data) {
        return {
          ...response,
          data: response.data.data
        };
      }
      return response;
    });
};

// 删除代理卡
export const deleteAgentCard = (agentId, cardId) => {
  return api.delete(`/agents/${agentId}/cards/${cardId}`)
    .then(response => {
      // 适配后端ApiResponse格式 - 删除操作返回void
      return response;
    });
};

// 增加代理卡数量
export const increaseAgentCard = (agentId, cardId, amount) => {
  return api.patch(`/agents/${agentId}/cards/${cardId}/increase`, { amount });
};

// 减少代理卡数量
export const decreaseAgentCard = (agentId, cardId, amount) => {
  return api.patch(`/agents/${agentId}/cards/${cardId}/decrease`, { amount });
};

// 获取代理卡详情（包含学科版本信息）
export const getAgentCardDetail = (agentId, cardId) => {
  return api.get(`/agents/${agentId}/cards/${cardId}/detail`);
};

// 获取卡原型详情
export const getCardPrototypeDetail = (prototypeId) => {
  return api.get(`/card-prototypes/${prototypeId}`);
};

// 获取卡类型选项（从后端卡原型API获取）
export const getCardTypes = async () => {
  const response = await axios.get('/card-prototypes/active');
  const prototypes = response.data || [];
  const cardTypes = prototypes.map(prototype => prototype.cardType);
  return { data: cardTypes };
};

/**
 * 获取督学账号列表
 * @param {Object} params - 查询参数，包括分页信息和regionId
 * @returns {Promise} - 返回督学账号列表
 */
export const getInspectorAccounts = (params) => {
  return api.get('/account/inspector', { params }).then(response => {
    if (response.data && Object.prototype.hasOwnProperty.call(response.data, 'content')) {
      return {
        ...response,
        data: {
          ...response.data,
          content: Array.isArray(response.data.content) ? response.data.content : []
        }
      };
    }
    return response;
  });
};

// 创建督学账号
export const createInspectorAccount = (data) => {
  return api.post('/account/inspector', data);
};

// 更新督学账号
export const updateInspectorAccount = (id, data) => {
  return api.put(`/account/inspector/${id}`, data);
};

// 删除督学账号
export const deleteInspectorAccount = (id) => {
  return api.delete(`/account/inspector/${id}`);
};

// 获取督学账号统计信息
export const getInspectorStats = (params) => {
  return api.get('/account/inspector/stats', params ? { params } : undefined);
};

// 获取督学名下的学生列表
export const getStudentsByInspectorId = (inspectorId, params) => {
  return api.get(`/account/inspector/${inspectorId}/students`, { params });
};

/**
 * 获取经销商账号列表
 * @param {Object} params - 查询参数，包括分页信息和regionId
 * @returns {Promise} - 返回经销商账号列表
 */
export const getDealerAccounts = (params) => {
  return api.get('/dealers', { params }).then(response => {
    if (response.data && response.data.content && Array.isArray(response.data.content)) {
      return response;
    }
    if (Array.isArray(response.data)) {
      return response;
    }
    return {
      ...response,
      data: {
        content: [],
        totalElements: 0,
        totalPages: 0,
        size: params?.size || 10,
        number: params?.page || 0
      }
    };
  });
};

// 创建经销商账号
export const createDealerAccount = (accountData) => {
  return api.post('/dealers', accountData);
};

// 更新经销商账号
export const updateDealerAccount = (id, accountData) => {
  return api.put(`/dealers/${id}`, accountData);
};

// 删除经销商账号
export const deleteDealerAccount = (id) => {
  return api.delete(`/dealers/${id}`);
};

// 获取经销商账号统计数据
export const getDealerStats = () => {
  return api.get('/dealers/stats');
};
